// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook for fetching purchase orders in search/autocomplete components.
 * Uses non-suspense mode to allow showing loading states in the UI.
 */
import { getPurchaseOrders } from '@/purchase-order/api/get-purchase-orders';
import { PurchaseOrderFilter } from '@/purchase-order/constants/status';
import { purchaseOrderListQueryKey } from '@/purchase-order/query-keys';
import { REFERENCE_DATA_QUERY_OPTIONS } from '@/utilities/query-profiles';
import { useQuery } from '@tanstack/react-query';

/**
 * Configuration options for the usePurchaseOrderSearch hook.
 */
interface UsePurchaseOrderSearchOptions {
  filter?: PurchaseOrderFilter;
}

/**
 * React hook for fetching purchase orders in search components.
 * Uses non-suspense query to enable loading state display.
 *
 * @param options - Configuration object for the hook
 * @returns Object containing purchaseOrders array and loading state
 */
export function usePurchaseOrderSearch(
  options: UsePurchaseOrderSearchOptions = {}
) {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: purchaseOrderListQueryKey(options.filter),
    queryFn: () => getPurchaseOrders(options.filter),
    ...REFERENCE_DATA_QUERY_OPTIONS,
  });

  return {
    purchaseOrders: data?.purchaseOrders ?? [],
    isLoading,
    isFetching,
  };
}
